import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ApexCharts from 'apexcharts';
import { useDemoCtx } from './DemoContext';
import { IrrForm } from './panels/IrrForm';
import {
    calcCentroid,
    getMoistStatus,
    getSiteWeather,
    MOIST_COLOR,
    MOIST_LABEL,
    WEATHER_ICON,
    WEATHER_LABEL,
    type SavedSite,
} from './demoStorage';

/* ── mock data (demo용 고정값) ── */
const TABS = ['기본정보', '대시보드', '지도', '관수이력', '데이터로그', '보고서'];

/* ══════════════════════════════════════════
   수분율 추이 ApexCharts 컴포넌트
══════════════════════════════════════════ */
function MoistChart({ moistData, rainData, statusColor }: {
    moistData: number[];
    rainData: number[];
    statusColor: string;
}) {
    const chartRef  = useRef<HTMLDivElement>(null);
    const chartInst = useRef<ApexCharts | null>(null);
    const [period, setPeriod] = useState<'14일' | '30일'>('30일');

    useEffect(() => {
        if (!chartRef.current) return;

        const data = period === '14일' ? moistData.slice(-14) : moistData;
        const rain = period === '14일' ? rainData.slice(-14) : rainData;
        const len  = data.length;
        const cats = Array.from({ length: len }, (_, i) => `${i + 1}일`);

        const opts = {
            series: [
                { name: '수분율(%)', type: 'area', data },
                { name: '강수량(mm)', type: 'column', data: rain },
            ],
            chart: {
                type: 'line',
                height: 240,
                toolbar: { show: false },
                dropShadow: {
                    enabled: true, top: 4, left: 0, blur: 6,
                    color: statusColor, opacity: 0.12,
                },
            },
            stroke: {
                curve: ['smooth', 'smooth'],
                width: [2, 0],
            },
            plotOptions: {
                bar: { columnWidth: '60%', borderRadius: 2 },
            },
            colors: [statusColor, '#93C5FD'],
            fill: {
                type: ['gradient', 'solid'],
                gradient: {
                    shade: 'light',
                    type: 'vertical',
                    opacityFrom: 0.38,
                    opacityTo: 0.03,
                    stops: [0, 90, 100],
                },
                opacity: [1, 0.65],
            },
            grid: {
                borderColor: '#f1f1f1',
                strokeDashArray: 3,
            },
            annotations: {
                yaxis: [
                    { y: 100, y2: 55, fillColor: '#D1FAE5', opacity: 0.2, borderColor: 'transparent' },
                    { y: 55, y2: 40, fillColor: '#FEF3C7', opacity: 0.28, borderColor: 'transparent' },
                    { y: 40, y2: 0, fillColor: '#FEE2E2', opacity: 0.2, borderColor: 'transparent' },
                    {
                        y: 55, borderColor: '#1E8B53', strokeDashArray: 4,
                        label: {
                            text: '양호 55%',
                            style: { color: '#1E8B53', background: '#D1FAE5', fontSize: '9px', fontWeight: 700, cssClass: '' },
                            position: 'left', offsetX: 68,
                        },
                    },
                    {
                        y: 40, borderColor: '#DC2626', strokeDashArray: 4,
                        label: {
                            text: '위험 40%',
                            style: { color: '#DC2626', background: '#FEE2E2', fontSize: '9px', fontWeight: 700, cssClass: '' },
                            position: 'left', offsetX: 68,
                        },
                    },
                ],
            },
            dataLabels: { enabled: false },
            legend: {
                show: true,
                position: 'top',
                markers: { size: 5, shape: 'circle' },
                fontSize: '11px',
            },
            xaxis: {
                categories: cats,
                tickAmount: 6,
                labels: { style: { fontSize: '10px' } },
                axisBorder: { show: false },
            },
            yaxis: [
                {
                    min: 0, max: 100,
                    title: { text: '수분율(%)', style: { fontSize: '10px' } },
                    labels: {
                        formatter: (v: number) => `${Math.round(v)}%`,
                        style: { fontSize: '9px' },
                    },
                },
                {
                    opposite: true,
                    min: 0, max: 30,
                    title: { text: '강수량(mm)', style: { fontSize: '10px' } },
                    labels: {
                        formatter: (v: number) => `${v}mm`,
                        style: { fontSize: '9px' },
                    },
                },
            ],
            tooltip: {
                shared: true, intersect: false,
                y: [
                    { formatter: (v: number) => `${v.toFixed(1)}%` },
                    { formatter: (v: number) => `${v}mm` },
                ],
            },
        };

        chartInst.current?.destroy();
        chartInst.current = new ApexCharts(chartRef.current, opts);
        chartInst.current.render();

        return () => {
            chartInst.current?.destroy();
            chartInst.current = null;
        };
    }, [moistData, rainData, statusColor, period]);

    return (
        <div className="data-card" style={{ marginBottom: 14 }}>
            <div className="data-card-hd">
                <span className="data-card-ttl">수분율 추이 (강수량 포함)</span>
                <div style={{ display: 'flex', gap: 4 }}>
                    {(['14일', '30일'] as const).map(p => (
                        <button
                            key={p}
                            className={`btn btn-sm ${period === p ? 'btn-primary' : 'btn-secondary'}`}
                            style={{ padding: '3px 10px', fontSize: 11 }}
                            onClick={() => setPeriod(p)}
                        >
                            {p}
                        </button>
                    ))}
                </div>
            </div>
            <div ref={chartRef} />
        </div>
    );
}

/* ══════════════════════════════════════════
   단지 보고서 컴포넌트
══════════════════════════════════════════ */
function SiteReport({ site, toast, openIrr }: {
    site: SavedSite;
    toast: (msg: string, type?: 'success' | 'error' | 'warning') => void;
    openIrr: () => void;
}) {
    const moist  = site.moist ?? 50;
    const st     = getMoistStatus(moist);
    const c      = MOIST_COLOR[st];
    const { temp: wxTemp, weather: wxType } = getSiteWeather(site);

    /* ── 결정론적 더미 데이터 (moist 기반) ── */
    const moistTrend = Array.from({ length: 30 }, (_, i) => {
        const wave = Math.sin(i * 0.7) * 6 + Math.sin(i * 1.2) * 3.5;
        const base = moist + (i - 15) * 0.35;
        return Math.max(8, Math.min(92, base + wave));
    });

    const wkIrr = [
        { wk: '1주', amt: 820 + moist * 5,  cnt: 2, bar: moist < 40 ? 'var(--danger)' : 'var(--primary)' },
        { wk: '2주', amt: 640 + moist * 3,  cnt: 1, bar: 'var(--primary)' },
        { wk: '3주', amt: 980 + moist * 7,  cnt: 3, bar: moist < 50 ? 'var(--warn)' : 'var(--primary)' },
        { wk: '4주', amt: 1200 + moist * 4, cnt: 2, bar: moist < 40 ? 'var(--danger)' : 'var(--caution)' },
    ];
    const totalAmt  = wkIrr.reduce((s, w) => s + w.amt, 0);
    const totalCnt  = wkIrr.reduce((s, w) => s + w.cnt, 0);
    const maxAmt    = Math.max(...wkIrr.map(w => w.amt));
    const prevAmt   = Math.round(totalAmt * 1.13);
    const savedWater = prevAmt - totalAmt;

    const rainfall  = [8, 0, 12, 3, 0, 0, 5, 0, 18, 2, 0, 0, 7, 0, 0, 4, 0, 0, 0, 9, 0, 3, 0, 0, 0, 14, 0, 0, 6, 0];
    const treeSpecies = [
        { name: site.form.treeName || '소나무', cnt: parseInt(site.form.treeCount || '20'), health: Math.min(98, moist + 18 + 12), stage: '생육 중', st: moist > 55 ? 'good' : moist > 40 ? 'caution' : 'danger' },
        { name: '왕벚나무', cnt: 15, health: 68, stage: '개화 준비', st: 'caution' },
        { name: '느티나무', cnt: 22, health: 83, stage: '생육 중',   st: 'good'    },
        { name: '은행나무', cnt: 8,  health: 47, stage: '스트레스',  st: 'warn'    },
        { name: '단풍나무', cnt: 11, health: 91, stage: '생육 중',   st: 'good'    },
    ];

    const costPerL     = 0.85;
    const totalCost    = Math.round(totalAmt * costPerL);
    const prevCost     = Math.round(prevAmt * costPerL);
    const costSaved    = prevCost - totalCost;
    const efficiency   = Math.round((savedWater / prevAmt) * 100);

    /* ── SVG 도넛 (관수 방법) ── */
    const R = 32, CX = 50, CY = 50;
    const circ = 2 * Math.PI * R;
    const donutSegs = [
        { lbl: '점적관수', pct: 0.52, color: '#1E8B53' },
        { lbl: '스프링클러', pct: 0.30, color: '#2563EB' },
        { lbl: '수동',     pct: 0.18, color: '#D97706' },
    ];
    let cumPct = 0;
    const donutPaths = donutSegs.map(seg => {
        const offset   = -circ * cumPct;
        const dashArr  = `${circ * seg.pct} ${circ}`;
        cumPct += seg.pct;
        return { ...seg, dashArr, offset };
    });

    const AIR = [
        { priority: '긴급', color: '#DC2626', bg: '#FEF2F2', ico: '🚨',
          title: '토양 수분율 위험 수준 감지',
          body: `현재 수분율 ${moist}%로 ${MOIST_LABEL[st]} 상태입니다. ${moist < 40 ? '72시간 이내 관수 미실시 시 수목 고사 위험이 있습니다.' : '정기 관수 일정을 앞당기는 것을 권장합니다.'}`,
          action: '즉시 관수 등록' },
        { priority: '권장', color: '#D97706', bg: '#FFFBEB', ico: '📊',
          title: '점적관수 전환으로 절수 효율 향상',
          body: `현재 스프링클러 방식 대비 점적관수 도입 시 월 평균 ${Math.round(totalAmt * 0.22).toLocaleString()}L 절수 가능. 연간 비용 절감 예상액 약 ${Math.round(costSaved * 12).toLocaleString()}원.`,
          action: '관수 방법 변경 검토' },
        { priority: '예측', color: '#2563EB', bg: '#EFF6FF', ico: '🌦️',
          title: '다음 주 강수 부재 예측 — 사전 관수 권장',
          body: `기상청 7일 예보 기준 강수 예보 없음. ${site.form.treeName || '수목'} 특성상 건조 스트레스 지수 상승 예상. 2~3일 이내 예방 관수를 권장합니다.`,
          action: '관수 일정 등록' },
        { priority: '인사이트', color: '#7C3AED', bg: '#F5F3FF', ico: '🤖',
          title: 'AI 생육 최적화 제안',
          body: `${treeSpecies[0].name} 생육 단계 분석 결과, 현재 토양 pH ${site.form.ph || '6.5'} 수준에서 관수 빈도를 주 2회로 조정 시 생육 효율 약 18% 개선 가능합니다.`,
          action: '관리 계획 수립' },
    ];

    const nextMonthPlan = [
        { week: '1주차 (2월 1~7일)',  action: '정기 관수 × 2회',  liters: '1,400L', type: '점적관수', risk: 'low'  },
        { week: '2주차 (2월 8~14일)', action: '집중 관수 × 3회',  liters: '2,100L', type: '점적관수', risk: 'high' },
        { week: '3주차 (2월 15~21일)',action: '정기 관수 × 2회',  liters: '1,200L', type: '스프링클러', risk: 'mid' },
        { week: '4주차 (2월 22~28일)',action: '정기 관수 × 1회',  liters: '700L',   type: '수동',     risk: 'low'  },
    ];

    return (
        <div style={{ padding: '20px 24px', overflowY: 'auto', height: '100%', background: '#F8FAF8' }}>

            {/* ── 헤더 ── */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div>
                    <div style={{ fontSize: 17, fontWeight: 800, color: '#1A2B1A' }}>단지 관리 보고서</div>
                    <div style={{ fontSize: 11.5, color: '#6B7280', marginTop: 2 }}>
                        {site.form.name} · 담당자 {site.form.mgr || '-'} · 보고 기준일 2026.01.31
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 7 }}>
                    <select className="form-select" style={{ height: 34, width: 130, fontSize: 12 }}>
                        <option>2026년 1월</option><option>2025년 12월</option><option>2025년 11월</option>
                    </select>
                    <button className="btn btn-ghost btn-sm" onClick={() => toast('인쇄 모드', 'success')}>🖨️ 인쇄</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => toast('Excel 다운로드', 'success')}>⬇ Excel</button>
                    <button className="btn btn-primary btn-sm" onClick={() => toast('PDF 다운로드', 'success')}>📄 PDF</button>
                </div>
            </div>

            {/* ── KPI 요약 ── */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 10, marginBottom: 16 }}>
                {[
                    { ico: '💧', lbl: '총 관수량', val: `${totalAmt.toLocaleString()}L`,   sub: `전월 대비 -${efficiency}%`,    sub_c: '#1E8B53' },
                    { ico: '📅', lbl: '관수 횟수', val: `${totalCnt}회`,                   sub: '이번달 누적',                   sub_c: '#6B7280' },
                    { ico: '🌱', lbl: '평균 수분율', val: `${moist}%`,                     sub: MOIST_LABEL[st],                sub_c: c         },
                    { ico: '💰', lbl: '절수 비용',  val: `${costSaved.toLocaleString()}원`, sub: `전월 대비 절감`,               sub_c: '#2563EB' },
                    { ico: '⚠️', lbl: 'AI 위험 알림', val: `${moist < 40 ? 3 : moist < 55 ? 1 : 0}건`, sub: moist < 40 ? '긴급 조치 필요' : '정상 범위', sub_c: moist < 40 ? '#DC2626' : '#1E8B53' },
                ].map((k, i) => (
                    <div key={i} style={{
                        background: '#fff', borderRadius: 12, padding: '12px 14px',
                        border: '1.5px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                    }}>
                        <div style={{ fontSize: 18, marginBottom: 4 }}>{k.ico}</div>
                        <div style={{ fontSize: 10.5, color: '#9CA3AF', fontWeight: 600, marginBottom: 2 }}>{k.lbl}</div>
                        <div style={{ fontSize: 16, fontWeight: 800, color: '#1A2B1A' }}>{k.val}</div>
                        <div style={{ fontSize: 10.5, color: k.sub_c, fontWeight: 600, marginTop: 2 }}>{k.sub}</div>
                    </div>
                ))}
            </div>

            {/* ── 수분율 월간 추이 ── */}
            <MoistChart moistData={moistTrend} rainData={rainfall} statusColor={c} />

            {/* ── 관수 현황 + 방법별 비율 ── */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                {/* 주간 관수 현황 */}
                <div className="data-card">
                    <div className="data-card-hd"><span className="data-card-ttl">주간 관수 현황</span>
                        <span style={{ fontSize: 10.5, color: '#6B7280' }}>총 {totalAmt.toLocaleString()}L · {totalCnt}회</span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', height: 100, paddingBottom: 4 }}>
                        {wkIrr.map((w, i) => {
                            const h = Math.round((w.amt / maxAmt) * 80);
                            return (
                                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                                    <span style={{ fontSize: 9, color: '#6B7280', fontWeight: 700 }}>{w.amt.toLocaleString()}L</span>
                                    <div style={{ width: '100%', height: h, background: w.bar, borderRadius: '4px 4px 0 0', transition: 'height 0.4s' }} />
                                    <span style={{ fontSize: 9.5, color: '#374151', fontWeight: 600 }}>{w.wk}</span>
                                    <span style={{ fontSize: 9, color: '#9CA3AF' }}>{w.cnt}회</span>
                                </div>
                            );
                        })}
                    </div>
                    <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', fontSize: 10.5 }}>
                        <span style={{ color: '#6B7280' }}>전월 관수량</span>
                        <span style={{ fontWeight: 700 }}>{prevAmt.toLocaleString()}L <span style={{ color: '#1E8B53' }}>→ -{efficiency}% 절감</span></span>
                    </div>
                </div>

                {/* 관수 방법별 비율 */}
                <div className="data-card">
                    <div className="data-card-hd"><span className="data-card-ttl">관수 방법별 비율</span></div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        <svg viewBox="0 0 100 100" style={{ width: 100, height: 100, flexShrink: 0 }}>
                            {donutPaths.map(seg => (
                                <circle key={seg.lbl}
                                    cx={CX} cy={CY} r={R}
                                    fill="none"
                                    stroke={seg.color}
                                    strokeWidth="14"
                                    strokeDasharray={seg.dashArr}
                                    strokeDashoffset={seg.offset}
                                    transform={`rotate(-90 ${CX} ${CY})`}
                                />
                            ))}
                            <circle cx={CX} cy={CY} r="20" fill="#fff" />
                            <text x={CX} y={CY + 1} textAnchor="middle" dominantBaseline="middle" fontSize="9" fontWeight="800" fill="#1A2B1A">{totalCnt}회</text>
                        </svg>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {donutPaths.map(seg => (
                                <div key={seg.lbl} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                                    <div style={{ width: 10, height: 10, borderRadius: 2, background: seg.color, flexShrink: 0 }} />
                                    <span style={{ fontSize: 11.5, flex: 1 }}>{seg.lbl}</span>
                                    <span style={{ fontSize: 11.5, fontWeight: 700 }}>{Math.round(seg.pct * 100)}%</span>
                                </div>
                            ))}
                            <div style={{ marginTop: 4, padding: '6px 8px', background: '#F0FDF4', borderRadius: 6, fontSize: 10.5, color: '#166840', fontWeight: 600 }}>
                                💡 점적관수 비중 ↑ 시 약 22% 절수 가능
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── 토양 분석 + 강수량 ── */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                {/* 토양 깊이별 수분 분석 */}
                <div className="data-card">
                    <div className="data-card-hd"><span className="data-card-ttl">토양 깊이별 수분 분석</span></div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {[
                            { depth: '표층 (0–10cm)',   pct: Math.max(5, moist - 8),  temp: wxTemp - 3, note: '증발 영향 큼' },
                            { depth: '중층 (10–20cm)',  pct: Math.max(5, moist),      temp: wxTemp - 1, note: '뿌리 주 흡수층' },
                            { depth: '심층 (20–30cm)',  pct: Math.min(95, moist + 6), temp: wxTemp,     note: '저수층' },
                            { depth: '기반 (30–40cm)',  pct: Math.min(95, moist + 11),temp: wxTemp + 1, note: '참조 기준' },
                        ].map((row, i) => {
                            const pct = row.pct;
                            const col = pct < 40 ? '#DC2626' : pct < 55 ? '#D97706' : '#1E8B53';
                            return (
                                <div key={i}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3, fontSize: 10.5 }}>
                                        <span style={{ color: '#374151', fontWeight: 600 }}>{row.depth}</span>
                                        <span style={{ color: '#9CA3AF' }}>{row.temp}°C · {row.note}</span>
                                        <span style={{ color: col, fontWeight: 700 }}>{pct}%</span>
                                    </div>
                                    <div style={{ height: 7, background: '#F3F4F6', borderRadius: 4, overflow: 'hidden' }}>
                                        <div style={{ height: '100%', width: `${pct}%`, background: col, borderRadius: 4, transition: 'width 0.4s' }} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div style={{ marginTop: 10, padding: '8px 10px', background: '#F8FAF8', borderRadius: 7, fontSize: 10.5, color: '#374151' }}>
                        <strong>토양 유형:</strong> {site.form.soilType || '양토'} &nbsp;·&nbsp;
                        <strong>pH:</strong> {site.form.ph || '6.5'} &nbsp;·&nbsp;
                        <strong>토심:</strong> {site.form.soilDepth || '30'}cm &nbsp;·&nbsp;
                        <strong>배수:</strong> {site.form.drainage || '양호'}
                    </div>
                </div>

                {/* 강수량 영향 */}
                <div className="data-card">
                    <div className="data-card-hd"><span className="data-card-ttl">월간 강수량 현황</span>
                        <span style={{ fontSize: 10.5, color: '#6B7280' }}>총 {rainfall.reduce((a, b) => a + b, 0)}mm</span>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                        <svg viewBox="0 0 300 80" style={{ width: '100%', display: 'block' }}>
                            {rainfall.map((r, i) => {
                                const bh = (r / 20) * 55;
                                const bx = i * 10 + 1;
                                return (
                                    <g key={i}>
                                        <rect x={bx} y={60 - bh} width={8} height={bh} fill="#2563EB" fillOpacity="0.65" rx="1" />
                                        {r > 0 && <text x={bx + 4} y={58 - bh} textAnchor="middle" fontSize="6" fill="#2563EB">{r}</text>}
                                    </g>
                                );
                            })}
                            <line x1="0" y1="62" x2="300" y2="62" stroke="#E5E7EB" strokeWidth="0.8" />
                            {[0, 9, 19, 29].map(i => (
                                <text key={i} x={i * 10 + 5} y="72" textAnchor="middle" fontSize="7" fill="#9CA3AF">{i + 1}일</text>
                            ))}
                        </svg>
                    </div>
                    <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        {[
                            { lbl: '최대 강수일', val: '26일 (14mm)' },
                            { lbl: '무강수 일수', val: `${rainfall.filter(r => r === 0).length}일` },
                            { lbl: '강수 후 수분 상승', val: '+12%' },
                            { lbl: '증발산 손실', val: `약 ${Math.round(totalAmt * 0.08).toLocaleString()}L` },
                        ].map(({ lbl, val }) => (
                            <div key={lbl} style={{ background: '#F8FAFC', borderRadius: 7, padding: '6px 8px' }}>
                                <div style={{ fontSize: 9.5, color: '#9CA3AF' }}>{lbl}</div>
                                <div style={{ fontSize: 12, fontWeight: 700, color: '#1A2B1A' }}>{val}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* ── 수종별 건강 지수 ── */}
            <div className="data-card" style={{ marginBottom: 14 }}>
                <div className="data-card-hd"><span className="data-card-ttl">수종별 건강 지수</span>
                    <span style={{ fontSize: 10.5, color: '#6B7280' }}>총 {treeSpecies.reduce((s, t) => s + t.cnt, 0)}주 등록</span>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11.5 }}>
                    <thead>
                        <tr style={{ borderBottom: '1.5px solid #E5E7EB' }}>
                            {['수종명', '식재수', '생육 단계', '건강 지수', '상태', '권장 조치'].map(h => (
                                <th key={h} style={{ padding: '6px 8px', textAlign: 'left', color: '#6B7280', fontWeight: 700, fontSize: 10.5 }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {treeSpecies.map((t, i) => {
                            const hc = t.health >= 80 ? '#1E8B53' : t.health >= 60 ? '#D97706' : '#DC2626';
                            return (
                                <tr key={i} style={{ borderBottom: '1px solid #F3F4F6', background: i % 2 === 0 ? '#FAFAFA' : '#fff' }}>
                                    <td style={{ padding: '7px 8px', fontWeight: 700 }}>🌳 {t.name}</td>
                                    <td style={{ padding: '7px 8px' }}>{t.cnt}주</td>
                                    <td style={{ padding: '7px 8px', color: '#374151' }}>{t.stage}</td>
                                    <td style={{ padding: '7px 8px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <div style={{ flex: 1, height: 5, background: '#F3F4F6', borderRadius: 3, overflow: 'hidden' }}>
                                                <div style={{ height: '100%', width: `${t.health}%`, background: hc, borderRadius: 3 }} />
                                            </div>
                                            <span style={{ color: hc, fontWeight: 700, minWidth: 30 }}>{t.health}%</span>
                                        </div>
                                    </td>
                                    <td style={{ padding: '7px 8px' }}>
                                        <span className={`badge badge-${t.st}`}>
                                            <span className={`badge-dot dot-${t.st}`} />{MOIST_LABEL[t.st as keyof typeof MOIST_LABEL]}
                                        </span>
                                    </td>
                                    <td style={{ padding: '7px 8px', color: '#6B7280', fontSize: 10.5 }}>
                                        {t.health >= 80 ? '정기 모니터링 유지' : t.health >= 60 ? '관수 빈도 증가 권장' : '긴급 집중 관수 필요'}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* ── 비용 분석 + 다음달 계획 ── */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                {/* 비용 분석 */}
                <div className="data-card">
                    <div className="data-card-hd"><span className="data-card-ttl">비용 분석</span></div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {[
                            { lbl: '이번달 용수비',   val: `${totalCost.toLocaleString()}원`,  sub: `${totalAmt.toLocaleString()}L × ${costPerL}원/L`,     c: '#1A2B1A' },
                            { lbl: '전월 용수비',     val: `${prevCost.toLocaleString()}원`,   sub: `${prevAmt.toLocaleString()}L 사용`,                   c: '#9CA3AF' },
                            { lbl: '절감 금액',       val: `▼ ${costSaved.toLocaleString()}원`, sub: `전월 대비 ${efficiency}% 절감`,                      c: '#1E8B53' },
                            { lbl: '연간 절감 예상',  val: `${(costSaved * 12).toLocaleString()}원`, sub: '현 추세 지속 시',                              c: '#2563EB' },
                        ].map(({ lbl, val, sub, c: vc }) => (
                            <div key={lbl} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 10px', background: '#F9FAFB', borderRadius: 7 }}>
                                <div>
                                    <div style={{ fontSize: 10.5, color: '#9CA3AF' }}>{lbl}</div>
                                    <div style={{ fontSize: 10, color: '#C0C4CC', marginTop: 1 }}>{sub}</div>
                                </div>
                                <div style={{ fontSize: 14, fontWeight: 800, color: vc }}>{val}</div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* 다음달 관수 계획 */}
                <div className="data-card">
                    <div className="data-card-hd"><span className="data-card-ttl">다음달 관수 계획 (2월)</span>
                        <span style={{ fontSize: 10.5, background: '#D1FAE5', color: '#166840', borderRadius: 5, padding: '2px 6px', fontWeight: 700 }}>AI 예측</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {nextMonthPlan.map((p, i) => {
                            const rc = p.risk === 'high' ? '#DC2626' : p.risk === 'mid' ? '#D97706' : '#1E8B53';
                            return (
                                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '7px 9px', background: '#F9FAFB', borderRadius: 7, borderLeft: `3px solid ${rc}` }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: 10.5, fontWeight: 700, color: '#374151', marginBottom: 2 }}>{p.week}</div>
                                        <div style={{ fontSize: 10, color: '#6B7280' }}>{p.action} · {p.type}</div>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontSize: 12, fontWeight: 800, color: '#1A2B1A' }}>{p.liters}</div>
                                        <div style={{ fontSize: 9.5, color: rc, fontWeight: 600 }}>{p.risk === 'high' ? '위험 주의' : p.risk === 'mid' ? '주의' : '정상'}</div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* ── AI 인사이트 ── */}
            <div className="data-card" style={{ marginBottom: 6 }}>
                <div className="data-card-hd" style={{ marginBottom: 12 }}>
                    <span className="data-card-ttl">🤖 AI 인사이트 & 권장 조치</span>
                    <span style={{ fontSize: 10.5, color: '#6B7280' }}>Greenmap AI · 분석 기준일 2026.01.31</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {AIR.map((a, i) => (
                        <div key={i} style={{ border: `1.5px solid ${a.color}22`, borderLeft: `4px solid ${a.color}`, borderRadius: 9, padding: '12px 14px', background: a.bg }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                                    <span style={{ fontSize: 18 }}>{a.ico}</span>
                                    <span style={{ fontSize: 12.5, fontWeight: 800, color: '#1A2B1A' }}>{a.title}</span>
                                </div>
                                <span style={{ fontSize: 10, fontWeight: 700, color: a.color, background: `${a.color}18`, borderRadius: 5, padding: '2px 7px', whiteSpace: 'nowrap' }}>
                                    {a.priority}
                                </span>
                            </div>
                            <p style={{ margin: '0 0 10px', fontSize: 11.5, color: '#374151', lineHeight: 1.6 }}>{a.body}</p>
                            <button
                                className="btn btn-sm"
                                style={{ border: `1.5px solid ${a.color}`, color: a.color, background: '#fff', fontWeight: 700, fontSize: 11 }}
                                onClick={() => a.priority === '긴급' ? openIrr() : toast(a.action, 'success')}
                            >
                                {a.action} →
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            {/* 푸터 */}
            <div style={{ textAlign: 'center', marginTop: 16, paddingTop: 16, borderTop: '1px solid #E5E7EB', fontSize: 10.5, color: '#9CA3AF' }}>
                본 보고서는 Greenmap AIS 시스템에 의해 자동 생성되었습니다 · 담당자: {site.form.mgr || '-'} · 보고 기준일 2026.01.31
            </div>
        </div>
    );
}

const WEATHER_FC = [
    { day: '월', ico: '☀️', temp: '24°' }, { day: '화', ico: '⛅', temp: '22°' },
    { day: '수', ico: '🌧️', temp: '18°' }, { day: '목', ico: '🌧️', temp: '17°' },
    { day: '금', ico: '⛅', temp: '20°' }, { day: '토', ico: '☀️', temp: '25°' }, { day: '일', ico: '☀️', temp: '26°' },
];

const IRR_HISTORY = [
    { date: '2026.01.05 09:30', type: '긴급 관수 시행', badge: 'danger', badge_txt: '긴급', amt: '1,240L', method: '점적관수', mins: '75분' },
    { date: '2025.12.24 14:00', type: '정기 관수',       badge: 'good',   badge_txt: '완료', amt: '760L',   method: '스프링클러', mins: '40분' },
    { date: '2025.12.10 10:15', type: '정기 관수',       badge: 'good',   badge_txt: '완료', amt: '420L',   method: '수동',       mins: '30분' },
];

const LOG_DATA = [
    { dt: '2026-01-15 14:00', v1: '35.2%', v2: '42.1%', v3: '48.3%', temp: '18.4°C', ec: '1.2 dS/m', ph: '6.4', id: 'SNS-B-001' },
    { dt: '2026-01-15 12:00', v1: '34.8%', v2: '41.6%', v3: '47.9%', temp: '20.1°C', ec: '1.3 dS/m', ph: '6.3', id: 'SNS-B-001' },
    { dt: '2026-01-15 10:00', v1: '36.1%', v2: '43.2%', v3: '49.1%', temp: '17.2°C', ec: '1.1 dS/m', ph: '6.5', id: 'SNS-B-001' },
    { dt: '2026-01-15 08:00', v1: '37.4%', v2: '44.0%', v3: '50.2%', temp: '14.8°C', ec: '1.2 dS/m', ph: '6.4', id: 'SNS-B-001' },
];

/* ── 단지 전용 Naver 지도 ── */
function SiteDetailMap({ site, mapTypeToggle = false }: { site: SavedSite; mapTypeToggle?: boolean }) {
    const containerRef = useRef<HTMLDivElement>(null);
    const mapRef       = useRef<any>(null);
    const [mapType, setMapType] = useState<'NORMAL' | 'SATELLITE'>('SATELLITE');

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const doInit = () => {
            if (!window.naver?.maps || !container || mapRef.current) return;
            const nm   = window.naver.maps;
            const cen  = calcCentroid(site.polygon);
            const moist = site.moist ?? 50;
            const st    = getMoistStatus(moist);
            const color = MOIST_COLOR[st];
            const { temp, weather } = getSiteWeather(site);

            const map = new nm.Map(container, {
                center: new nm.LatLng(cen.lat, cen.lng),
                zoom: 17,
                minZoom: 6,
                maxZoom: 21,
                mapTypeId: nm.MapTypeId.SATELLITE,
                mapDataControl: false,
                scaleControl: false,
                logoControl: true,
                zoomControl: true,
                zoomControlOptions: {
                    style: nm.ZoomControlStyle.SMALL,
                    position: nm.Position.TOP_LEFT,
                },
            });
            new nm.CadastralLayer().setMap(map);
            mapRef.current = map;

            /* 폴리곤 */
            new nm.Polygon({
                map,
                paths: site.polygon.map(p => new nm.LatLng(p.lat, p.lng)),
                fillColor: color,
                fillOpacity: 0.22,
                strokeColor: color,
                strokeWeight: 2.5,
                strokeOpacity: 0.9,
                zIndex: 10,
            });

            /* 레이블 마커 */
            new nm.Marker({
                position: new nm.LatLng(cen.lat, cen.lng),
                map,
                icon: {
                    content: `
                        <div style="background:rgba(255,255,255,0.96);border:2px solid ${color};
                            border-radius:8px;padding:5px 10px;
                            font-size:11px;font-weight:800;color:#1A2B1A;
                            white-space:nowrap;font-family:Pretendard,-apple-system,sans-serif;
                            box-shadow:0 2px 8px rgba(0,0,0,0.15);line-height:1.6;cursor:default;">
                            <div style="display:flex;justify-content:space-between;gap:10px;">
                                <span>${site.form.name || '(미입력)'}</span>
                                <span>${WEATHER_ICON[weather]} ${temp}°C</span>
                            </div>
                            <div>
                                <span style="color:${color};font-size:9.5px;">${moist}% · ${MOIST_LABEL[st]}</span>
                                <span style="color:#9CA3AF;font-size:9px;"> · ${site.form.area || '-'}㎡</span>
                            </div>
                        </div>`,
                    anchor: new nm.Point(65, 22),
                },
                zIndex: 15,
            });
        };

        if (window.naver?.maps) {
            doInit();
        } else {
            const existing = document.querySelector<HTMLScriptElement>('script[src*="openapi.map.naver.com"]');
            if (existing) {
                existing.addEventListener('load', () => setTimeout(doInit, 100));
            } else {
                const key = import.meta.env.VITE_NCP_API_KEY_ID ?? '';
                const s = document.createElement('script');
                s.src = `https://openapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${key}&submodules=geocoder,drawing,geometry`;
                s.async = true;
                s.onload = () => setTimeout(doInit, 100);
                document.head.appendChild(s);
            }
        }

        return () => { mapRef.current = null; };
    }, [site]);

    const switchType = (t: 'NORMAL' | 'SATELLITE') => {
        if (!mapRef.current || !window.naver?.maps) return;
        mapRef.current.setMapTypeId(window.naver.maps.MapTypeId[t]);
        setMapType(t);
    };

    return (
        <div style={{ width: '100%', height: '100%', position: 'relative' }}>
            <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
            {mapTypeToggle && (
                <div style={{
                    position: 'absolute', bottom: 16, right: 16, zIndex: 100,
                    display: 'flex', borderRadius: 8, overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.18)', border: '1.5px solid #E2E8E2',
                }}>
                    {(['NORMAL', 'SATELLITE'] as const).map(t => (
                        <button key={t} onClick={() => switchType(t)} style={{
                            padding: '6px 14px', fontSize: 12, fontWeight: 700,
                            fontFamily: 'Pretendard,-apple-system,sans-serif',
                            border: 'none', cursor: 'pointer',
                            background: mapType === t ? '#1E8B53' : '#fff',
                            color: mapType === t ? '#fff' : '#4A6B4A',
                            transition: 'background 0.15s,color 0.15s',
                        }}>
                            {t === 'NORMAL' ? '일반' : '위성'}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

/* ── 메인 페이지 ── */
export default function DemoSiteDetail() {
    const { id }      = useParams<{ id: string }>();
    const name        = decodeURIComponent(id ?? '');
    const navigate    = useNavigate();
    const { toast, openPanel, savedSites } = useDemoCtx();
    const [tab, setTab] = useState(1);

    const site = savedSites.find(s => s.form.name === name) ?? savedSites[0];

    if (!site) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: 12 }}>
                <div style={{ fontSize: 48 }}>🌲</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text2)' }}>단지 정보를 찾을 수 없습니다</div>
                <button className="btn btn-primary btn-sm" onClick={() => navigate('/demo/sites')}>← 목록으로</button>
            </div>
        );
    }

    const moist  = site.moist ?? 50;
    const st     = getMoistStatus(moist);
    const c      = MOIST_COLOR[st];
    const { temp: wxTemp, weather: wxType } = getSiteWeather(site);

    const openIrr = () => openPanel({ title: '관수 등록', body: <IrrForm siteName={site.form.name} /> });

    const circumference = 2 * Math.PI * 37;
    const offset        = circumference - (moist / 100) * circumference;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ padding: '14px 20px', borderBottom: '1.5px solid var(--border)', background: 'var(--surface)', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => navigate('/demo/sites')}>← 목록</button>
                <div style={{ fontSize: 15, fontWeight: 800 }}>{site.form.name || '(미입력)'}</div>
                <span className={`badge badge-${st}`}><span className={`badge-dot dot-${st}`} />{MOIST_LABEL[st]}</span>
                <div style={{ flex: 1 }} />
                <button className="btn btn-primary" onClick={openIrr}>💧 관수 등록</button>
            </div>

            {/* Tabs */}
            <div className="detail-tabs">
                {TABS.map((t, i) => (
                    <div key={i} className={`detail-tab${tab === i ? ' active' : ''}`} onClick={() => setTab(i)}>{t}</div>
                ))}
            </div>

            {/* Content */}
            <div style={{ flex: 1, overflow: 'hidden' }}>

                {/* 기본정보 */}
                {tab === 0 && (
                    <div style={{ padding: 20, overflowY: 'auto', height: '100%' }}>
                        <div style={{ maxWidth: 640, display: 'flex', flexDirection: 'column', gap: 16 }}>
                            <div className="data-card">
                                <div className="data-card-hd"><span className="data-card-ttl">기본 정보</span><button className="btn btn-secondary btn-sm">편집</button></div>
                                <div className="form-row" style={{ marginBottom: 13 }}>
                                    <div className="form-group"><label className="form-label">생육단지명</label><input className="form-input" defaultValue={site.form.name} /></div>
                                    <div className="form-group"><label className="form-label">면적</label><input className="form-input" defaultValue={site.form.area ? `${site.form.area}㎡` : ''} /></div>
                                </div>
                                <div className="form-row" style={{ marginBottom: 13 }}>
                                    <div className="form-group"><label className="form-label">주소</label><input className="form-input" defaultValue={site.form.addr} /></div>
                                    <div className="form-group"><label className="form-label">담당자</label><input className="form-input" defaultValue={site.form.mgr} /></div>
                                </div>
                                <button className="btn btn-primary btn-sm" onClick={() => toast('저장 완료', 'success')}>변경사항 저장</button>
                            </div>
                            <div className="data-card">
                                <div className="data-card-hd"><span className="data-card-ttl">식재 정보</span></div>
                                <div style={{ display: 'flex', gap: 12 }}>
                                    <div className="metric-item"><div className="metric-lbl">주요 수종</div><div className="metric-val" style={{ fontSize: 13 }}>{site.form.treeName || '-'}</div></div>
                                    <div className="metric-item"><div className="metric-lbl">총 식재수</div><div className="metric-val">{site.form.treeCount ? `${site.form.treeCount}주` : '-'}</div></div>
                                    <div className="metric-item"><div className="metric-lbl">토양 유형</div><div className="metric-val" style={{ fontSize: 13 }}>{site.form.soilType || '-'}</div></div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* 대시보드 */}
                {tab === 1 && (
                    <div style={{ display: 'grid', gridTemplateColumns: '60% 40%', height: '100%', overflow: 'hidden' }}>
                        {/* Left: Naver 지도 */}
                        <div style={{ borderRight: '1.5px solid var(--border)', position: 'relative' }}>
                            <SiteDetailMap site={site} />
                        </div>

                        {/* Right: data panel */}
                        <div style={{ overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                <div className="metric-item"><div className="metric-lbl">면적</div><div className="metric-val" style={{ fontSize: 14 }}>{site.form.area || '-'}㎡</div></div>
                                <div className="metric-item"><div className="metric-lbl">총 식재수</div><div className="metric-val">{site.form.treeCount ? `${site.form.treeCount}주` : '-'}</div></div>
                            </div>

                            {/* Weather */}
                            <div className="data-card">
                                <div className="data-card-hd"><span className="data-card-ttl">현재 날씨</span></div>
                                <div className="wx-top">
                                    <div className="wx-icon">{WEATHER_ICON[wxType]}</div>
                                    <div className="wx-temp">{wxTemp}°C</div>
                                    <div>
                                        <div className="wx-desc">{WEATHER_LABEL[wxType]}</div>
                                        <div className="wx-detail">습도 52% · 풍속 2.3m/s</div>
                                    </div>
                                </div>
                                <div className="fc-row">
                                    {WEATHER_FC.map((w, i) => (
                                        <div key={i} className="fc-item">
                                            <div className="fc-day">{w.day}</div>
                                            <div className="fc-icon">{w.ico}</div>
                                            <div className="fc-temp">{w.temp}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Gauge */}
                            <div className="data-card">
                                <div className="data-card-hd"><span className="data-card-ttl">수분 상태</span></div>
                                <div className="gauge-wrap" style={{ marginBottom: 12 }}>
                                    <div className="gauge-ring">
                                        <svg viewBox="0 0 84 84">
                                            <circle className="g-bg" cx={42} cy={42} r={37} />
                                            <circle className="g-fill" cx={42} cy={42} r={37} stroke={c} strokeDasharray={circumference} strokeDashoffset={offset} />
                                        </svg>
                                        <div className="gauge-val">
                                            <div className="gauge-num" style={{ color: c }}>{moist}</div>
                                            <div className="gauge-unit">%</div>
                                        </div>
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 4 }}>{MOIST_LABEL[st]} 상태</div>
                                        <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.6 }}>
                                            권고 수분율 55~75%<br />
                                            현재 {moist < 40 ? '즉시 관수 필요' : moist < 55 ? '주의 관찰 필요' : '양호'}
                                        </div>
                                    </div>
                                </div>
                                <div className="metric-grid">
                                    <div className="metric-item">
                                        <div className="metric-lbl">건조 스트레스</div>
                                        <div className="metric-val" style={{ color: c }}>{moist < 40 ? '7.2' : moist < 55 ? '4.1' : '1.8'}</div>
                                        <div className="metric-sub">기준 6.0 이상 위험</div>
                                    </div>
                                    <div className="metric-item">
                                        <div className="metric-lbl">오늘 권고 관수량</div>
                                        <div className="metric-val">{moist < 40 ? '1,200L' : moist < 55 ? '620L' : '—'}</div>
                                        <div className="metric-sub">{moist < 40 ? '긴급' : moist < 55 ? '예정' : '불필요'}</div>
                                    </div>
                                </div>
                            </div>

                            <div className={`hero-box hero-box-${moist < 40 ? 'danger' : moist < 55 ? 'warn' : 'good'}`}>
                                <div className="hero-status" style={{ color: c }}>
                                    {moist < 40 ? '⚠️ 긴급 관수 필요' : moist < 55 ? '💧 관수 권장' : '✅ 양호 상태'}
                                </div>
                                <div className="hero-desc">
                                    {moist < 40 ? '토양 수분율이 위험 수준입니다. 즉시 관수를 실시해 주세요.' : moist < 55 ? '수분율이 주의 구간에 있습니다. 3일 이내 관수가 권장됩니다.' : '현재 수분율이 적정 범위에 있습니다.'}
                                </div>
                                <button className="btn btn-primary w-full" onClick={openIrr}>💧 관수 등록하기</button>
                            </div>

                            <div className="data-card">
                                <div className="data-card-hd"><span className="data-card-ttl">토양 깊이별 수분</span></div>
                                <div className="soil-layers">
                                    {[{ depth: '0-10cm', pct: moist - 6 }, { depth: '10-20cm', pct: moist + 1 }, { depth: '20-30cm', pct: moist + 8 }].map((row, i) => (
                                        <div key={i} className="soil-row">
                                            <div className="soil-depth">{row.depth}</div>
                                            <div className="soil-bar-bg"><div className="soil-bar-fill" style={{ width: `${Math.max(0, Math.min(100, row.pct))}%`, background: row.pct < 40 ? 'var(--warn)' : row.pct < 55 ? 'var(--caution)' : 'var(--good)' }} /></div>
                                            <div className="soil-pct" style={{ color: row.pct < 40 ? 'var(--warn)' : 'var(--good)' }}>{row.pct}%</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* 지도 */}
                {tab === 2 && (
                    <div style={{ height: '100%', position: 'relative' }}>
                        <SiteDetailMap site={site} mapTypeToggle />
                        {/* 단지 정보 오버레이 */}
                        <div style={{
                            position: 'absolute', top: 12, right: 14, zIndex: 100,
                            background: 'rgba(255,255,255,0.96)', border: `2px solid ${c}`,
                            borderRadius: 10, padding: '10px 14px', minWidth: 180,
                            fontFamily: 'Pretendard,-apple-system,sans-serif',
                            boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                        }}>
                            <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 4 }}>{site.form.name || '(미입력)'}</div>
                            <div style={{ fontSize: 10.5, color: '#6B7280', marginBottom: 8 }}>{site.form.addr || '-'}</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 11 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ color: '#9CA3AF' }}>수분율</span>
                                    <span style={{ fontWeight: 700, color: c }}>{moist}% · {MOIST_LABEL[st]}</span>
                                </div>
                                <div style={{ height: 4, background: '#E5E7EB', borderRadius: 2, overflow: 'hidden' }}>
                                    <div style={{ height: '100%', width: `${moist}%`, background: c, borderRadius: 2 }} />
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ color: '#9CA3AF' }}>면적</span>
                                    <span style={{ fontWeight: 600 }}>{site.form.area || '-'}㎡</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ color: '#9CA3AF' }}>기온</span>
                                    <span style={{ fontWeight: 600 }}>{WEATHER_ICON[wxType]} {wxTemp}°C</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ color: '#9CA3AF' }}>담당자</span>
                                    <span style={{ fontWeight: 600 }}>👤 {site.form.mgr || '-'}</span>
                                </div>
                            </div>
                            <button className="btn btn-primary btn-sm w-full" style={{ marginTop: 10 }} onClick={openIrr}>💧 관수 등록</button>
                        </div>
                    </div>
                )}

                {/* 관수이력 */}
                {tab === 3 && (
                    <div style={{ padding: 20, overflowY: 'auto', height: '100%' }}>
                        <div className="timeline">
                            {IRR_HISTORY.map((r, i) => (
                                <div key={i} className={`tl-item fade-up d${i}`}>
                                    <div className="tl-dot">💧</div>
                                    <div className="tl-body">
                                        <div className="tl-meta">{r.date} · {site.form.mgr || '-'}</div>
                                        <div className="tl-card">
                                            <div className="tl-card-top">
                                                <span className="tl-card-name">{r.type}</span>
                                                <span className={`badge badge-${r.badge}`}>{r.badge_txt}</span>
                                            </div>
                                            <div className="tl-stats">
                                                <div className="tl-stat"><div className="tl-stat-lbl">관수량</div><div className="tl-stat-val">{r.amt}</div></div>
                                                <div className="tl-stat"><div className="tl-stat-lbl">관수 방법</div><div className="tl-stat-val" style={{ fontSize: 11 }}>{r.method}</div></div>
                                                <div className="tl-stat"><div className="tl-stat-lbl">소요 시간</div><div className="tl-stat-val">{r.mins}</div></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* 데이터로그 */}
                {tab === 4 && (
                    <div style={{ padding: 20, overflowY: 'auto', height: '100%' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                            <div style={{ fontSize: 15, fontWeight: 800 }}>센서 데이터 로그</div>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <select className="form-select" style={{ height: 34, width: 120, fontSize: 12 }}><option>수분율</option><option>온도</option><option>EC</option></select>
                                <button className="btn btn-secondary btn-sm" onClick={() => toast('CSV 다운로드', 'success')}>⬇ CSV</button>
                            </div>
                        </div>
                        <table className="dl-tbl">
                            <thead><tr><th>일시</th><th>0-10cm</th><th>10-20cm</th><th>20-30cm</th><th>토양온도</th><th>EC</th><th>pH</th><th>센서ID</th></tr></thead>
                            <tbody>
                                {LOG_DATA.map((row, i) => (
                                    <tr key={i}>
                                        <td>{row.dt}</td>
                                        <td style={{ color: 'var(--warn)' }}>{row.v1}</td>
                                        <td style={{ color: 'var(--warn)' }}>{row.v2}</td>
                                        <td style={{ color: 'var(--good)' }}>{row.v3}</td>
                                        <td>{row.temp}</td><td>{row.ec}</td><td>{row.ph}</td><td>{row.id}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div className="pagination" style={{ marginTop: 8 }}>
                            {['◀', '1', '2', '▶'].map((p, i) => <button key={i} className={`pg-btn${p === '1' ? ' active' : ''}`} onClick={() => toast(`${p} 페이지`)}>{p}</button>)}
                        </div>
                    </div>
                )}

                {/* 보고서 */}
                {tab === 5 && <SiteReport site={site} toast={toast} openIrr={openIrr} />}
            </div>
        </div>
    );
}
